/**
 * FaqDisclosureList — the FAQ block whose answers are IN the document.
 *
 * ## The bug this element shape retires
 *
 * Every public FAQ (home, services, the school map, and the mortgage,
 * stamp-duty and affordability calculators) rendered through the Radix
 * `Accordion`. Radix's collapsible content renders `isOpen && children` — so a
 * COLLAPSED item emits `<div data-state="closed" hidden></div>` with **nothing
 * inside it**. Every answer was absent from the server HTML, and since these
 * pages ship their domain explanation in the answers, the crawlable body was
 * the questions alone. Measured on the live mortgage calculator: 8 answers,
 * 427 zh-HK characters and ~1,300 en characters, none of them in the response.
 * The `FAQPage` JSON-LD, meanwhile, carried all of it — a page describing
 * content it did not serve.
 *
 * `forceMount` does not fix it: Radix then reports the item permanently
 * present, so `hidden` never applies and every answer renders expanded.
 *
 * A native `<details>`/`<summary>` disclosure has no such state. The answer is
 * an unconditional child of the DOM, open or closed; the browser does the
 * expand/collapse, the keyboard and screen-reader semantics come for free, and
 * the whole block needs no client JavaScript — so it is also correct before
 * hydration and with JS disabled. "The answer is missing from the HTML" stops
 * being a thing that can happen here.
 *
 * The `items` come in as a {@link PageFaqs} — the same value the page hands to
 * `SeoHead` as structured data — so the rendered answers and the described
 * answers are one array (see `shared/lib/seo/faq-jsonld`).
 */
import type { PageFaqs } from '@/shared/lib/seo/faq-jsonld';
import { ChevronDownIcon } from 'lucide-react';

export function FaqDisclosureList({ faqs }: { faqs: PageFaqs }) {
  return (
    <div className="w-full">
      {faqs.items.map((faq) => (
        <details
          key={faq.question}
          className="group border-b border-border last:border-b-0 [&_summary::-webkit-details-marker]:hidden"
        >
          <summary className="flex cursor-pointer list-none items-start justify-between gap-4 rounded-md py-4 text-left text-sm font-medium outline-none hover:underline focus-visible:ring-[3px] focus-visible:ring-ring/50">
            {faq.question}
            <ChevronDownIcon className="pointer-events-none size-4 shrink-0 translate-y-0.5 text-muted-foreground transition-transform duration-200 group-open:rotate-180" />
          </summary>
          <div className="pt-0 pb-4 text-sm text-muted-foreground">{faq.answer}</div>
        </details>
      ))}
    </div>
  );
}
